import { TagColor, TagModel } from "client/Components";
import { normalizeForSearch } from "client/Data/searching";
import { TagSaveModel } from "client/Api/Tags/tags";
import { isNotNil, resolveFormattedTextString } from "client/Misc/utils";
import { InputValidationResultKind, IValueValidation } from "client/Validation/input";
import { localize } from "client_legacy/ndaw/culture";
import { IValueReducer } from "../reducer";

export interface IAttachableTagSelectProps<TValue extends number> {
	readonly value: readonly TagModel<TValue>[];
	readonly isInvalid: boolean;
	readonly trackingId: string;
	readonly maxTagLength: number | undefined;
	onAdd(newTag: TagModel<TValue>): Promise<boolean>;
	onDelete?(index: number): void;
}

export class AttachableTagSelectProps<TValue extends number> implements IAttachableTagSelectProps<TValue> {
	private readonly _maxTagCount: number | undefined;
	private readonly _maxTagLength: number | undefined;
	private readonly _reducer: IValueReducer<readonly TagSaveModel[]>;
	private readonly _validation: IValueValidation<readonly TagSaveModel[]>;
	private readonly _onError: (message: string) => void;

	public readonly value: readonly TagModel<TValue>[];
	public readonly isInvalid: boolean;
	public readonly trackingId: string;
	public readonly maxTagLength: number | undefined;
	public onDelete?(index: number): void;

	constructor(
		reducer: IValueReducer<readonly TagSaveModel[]>,
		validation: IValueValidation<readonly TagSaveModel[]>,
		maxTagCount: number | undefined,
		maxTagLength: number | undefined,
		doesSupportDelete: boolean,
		color: TagColor | undefined,
		onError: (message: string) => void
	) {
		this._reducer = reducer;
		this._validation = validation;

		this.value = reducer.value
			.map((x: TagSaveModel): TagModel<TValue> => ({
				text: x.name,
				value: x.id as TValue,
				color: color
			}));

		this.isInvalid = validation
			? validation.getValidationState().kind === InputValidationResultKind.Invalid
			: false;

		this.trackingId = reducer.path;
		this.maxTagLength = maxTagLength;
		this._maxTagCount = maxTagCount;
		this._maxTagLength = maxTagLength;
		this._onError = onError;

		this.onDelete = doesSupportDelete
			? this.onDeleteImplementation.bind(this)
			: undefined;
		this.onAdd = this.onAdd.bind(this);
	}

	public onAdd(newTag: TagModel<TValue>): Promise<boolean> {
		const {
			value: newTagValue,
			text: newTagText
		} = newTag;

		const selectedTags: readonly TagModel<TValue>[] = this.value;

		if (isNotNil(this._maxTagCount) && selectedTags.length >= this._maxTagCount) {
			this._onError(
				resolveFormattedTextString({
					template: localize("_TheMaximumAmountIsMaxAddedTags"),
					replacements: [{
						what: "{{MaxAddedTags}}",
						filler: this._maxTagCount.toString(10)
					}]
				})
			);

			return Promise.resolve(false);
		}

		if (isNotNil(this._maxTagLength) && newTagText.length > this._maxTagLength) {
			this._onError(
				resolveFormattedTextString({
					template: localize("_TheMaximumLengthIsAmountCharacters"),
					replacements: [
						{
							what: "{{amount}}",
							filler: this._maxTagLength.toString(10)
						}
					]
				})
			);

			return Promise.resolve(false);
		}

		const isAlreadyAdded: boolean = selectedTags
			.some(
				x => normalizeForSearch(x.text) === normalizeForSearch(newTagText)
			);

		if (isAlreadyAdded) {
			this._onError(
				resolveFormattedTextString({
					template: localize("_TheTagTagNameHasAlreadyBeenAdded"),
					replacements: [
						{
							what: "{{TagName}}",
							filler: newTagText
						}
					]
				})
			);

			return Promise.resolve(false);
		}

		this._reducer
			.push({
				id: newTagValue,
				name: newTagText
			});

		this._validation
			.triggerInputFinished();

		return Promise.resolve(true);
	}

	private onDeleteImplementation(index: number): void {
		this._reducer
			.removeAt(index);

		this._validation
			.triggerInputFinished();
	}
}

export type AttachTagSelectOptions = {
	onError(message: string): void;
	maxTagCount?: number;
	maxTagLength?: number;
	doesSupportDelete?: boolean;
	color?: TagColor;
};